// ---------------------------------------------------------
// Datei: usePhotoAnnotationGallery.ts
// Zweck: Gemeinsame Zustandsmaschine für rückwirkendes Markieren/Beschriften von Fotos
//        (PhotoAnnotationEditor), extrahiert aus ProjektvorbereitungBriefing.tsx (Ticket #928),
//        damit auch die VOT-eigene Fotogalerie (ProjektdokumentationWizard.tsx, Ticket #1055)
//        dieselbe Öffnen/Speichern/Löschen/Abbrechen-Logik nutzen kann, ohne sie zu duplizieren.
// Kontext: Original wird nie überschrieben — die Markierung landet als eigenständige
//          "<original>-markiert"-Datei im selben Ordner (uploadMarkedPhotoAsync,
//          replaceIfExists: true). Der Hook kennt die Foto-LISTE des Aufrufers nicht (VOT und
//          Projektvorbereitung verwalten ihre Fotos in völlig unterschiedlichen State-Formen) —
//          er ruft nach erfolgreichem Speichern/Löschen nur onSaved/onDeleted auf, der Aufrufer
//          aktualisiert seinen eigenen State selbst (exakter Rückbau des bisherigen Verhaltens in
//          ProjektvorbereitungBriefing.tsx).
// Stand: Pairing-/Gruppierungs-Helfer (nach Herkunfts-Ordner) bleiben bewusst NICHT hier, da sie
//        präsentationsspezifisch für Projektvorbereitung sind (Allgemein/VOT/Sonstiges) — nur die
//        namensbasierten Grundhelfer (withMarkedFileNameSuffix/isMarkedFileName) sind gemeinsam.
// ---------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import { deleteFileAsync, getFileAsync, uploadMarkedPhotoAsync, type UploadMarkedPhotoResult } from '../services/api/drive';

export interface AnnotatablePhoto {
  id: string;
  name: string;
}

export interface PhotoPair<T extends AnnotatablePhoto> {
  original: T;
  // Nur gesetzt, wenn zu diesem Original bereits eine eigenständige "-markiert"-Datei existiert.
  marked?: T;
}

// Markiert bearbeitete Fotos auch im Dateinamen (nicht nur per Caption), damit man beim direkten
// Öffnen des Drive-Ordners außerhalb der App sofort sieht, welche Fotos schon markiert wurden.
// Idempotent, damit erneutes Bearbeiten nicht "-markiert-markiert" anhängt.
export const MARKED_FILE_SUFFIX = '-markiert';

export const withMarkedFileNameSuffix = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const base = lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;
  const extension = lastDotIndex > 0 ? fileName.slice(lastDotIndex) : '';
  if (base.endsWith(MARKED_FILE_SUFFIX)) {
    return fileName;
  }
  return `${base}${MARKED_FILE_SUFFIX}${extension}`;
};

export const isMarkedFileName = (fileName: string): boolean => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const base = lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;
  return base.endsWith(MARKED_FILE_SUFFIX);
};

interface UsePhotoAnnotationGalleryOptions<T extends AnnotatablePhoto> {
  projectId: string;
  // Projektvorbereitung leitet das meist aus dem Herkunfts-Ordner ab, VOT nutzt immer
  // ['Bilder', 'VOT'] — Rohranschluss-Fotos (Ticket #1055) kennen stattdessen nur die echte
  // Drive-FolderId (aus GetDriveFileMetadataAsync im Backend), keinen Directories-Pfad.
  getUploadTarget: (original: T) => string[] | { folderId: string };
  // Aufrufer baut aus dem Upload-Ergebnis sein eigenes Foto-Objekt und aktualisiert seine Liste.
  // annotatedDataUrl liegt bereits lokal vor — der Aufrufer kann sie direkt als Thumbnail
  // übernehmen, statt sie erneut übers Backend zu laden.
  onSaved: (
    result: UploadMarkedPhotoResult,
    caption: string,
    pair: PhotoPair<T>,
    annotatedDataUrl: string
  ) => void;
  onDeleted: (markedId: string, pair: PhotoPair<T>) => void;
  onError: (message: string) => void;
  onSuccess?: (message: string) => void;
}

export function usePhotoAnnotationGallery<T extends AnnotatablePhoto>({
  projectId,
  getUploadTarget,
  onSaved,
  onDeleted,
  onError,
  onSuccess,
}: UsePhotoAnnotationGalleryOptions<T>) {
  const [editingPair, setEditingPair] = useState<PhotoPair<T> | null>(null);
  const [editorImageDataUrl, setEditorImageDataUrl] = useState<string | null>(null);
  // Nur gesetzt, wenn editingPair.marked existiert — das unangetastete Original, zu dem
  // "Löschen" im Editor zurückspringt.
  const [editorOriginalImageDataUrl, setEditorOriginalImageDataUrl] = useState<string | undefined>(
    undefined
  );
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [savingAnnotation, setSavingAnnotation] = useState(false);

  // Hält die ID des Fotos, dessen Ladevorgang aktuell "gilt" — ohne das könnte man Foto A öffnen,
  // den noch ladenden Dialog schließen und Foto B öffnen: A's Promise wird dadurch nicht
  // abgebrochen und würde nach dem Auflösen die gemeinsamen editorImageDataUrl/-Loading/-Error-
  // States überschreiben, obwohl editingPair längst B ist (Autoreview-Finding). Jeder set...-Call
  // nach dem `await` prüft deshalb, ob die eigene requestId noch aktuell ist.
  const activeAnnotationRequestIdRef = useRef<string | null>(null);

  const cancelAnnotation = () => {
    activeAnnotationRequestIdRef.current = null;
    setEditingPair(null);
    setEditorImageDataUrl(null);
    setEditorOriginalImageDataUrl(undefined);
    setEditorError(null);
  };

  // Modal schließen bei ESC, Hintergrund-Scroll sperren solange der Editor offen ist.
  useEffect(() => {
    if (!editingPair) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        cancelAnnotation();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingPair]);

  const openAnnotation = async (pair: PhotoPair<T>) => {
    const displayFoto = pair.marked ?? pair.original;
    const requestId = displayFoto.id;
    activeAnnotationRequestIdRef.current = requestId;

    setEditingPair(pair);
    setEditorImageDataUrl(null);
    setEditorOriginalImageDataUrl(undefined);
    setEditorError(null);
    setEditorLoading(true);
    try {
      // Zunächst die bereits markierte Version anzeigen, falls vorhanden — das Original wird nur
      // zusätzlich geladen, damit "Löschen" sofort darauf zurückspringen kann (kein erneuter
      // Server-Roundtrip beim Klick auf "Löschen" nötig).
      const [displayContent, originalContent] = await Promise.all([
        getFileAsync(displayFoto.id),
        pair.marked ? getFileAsync(pair.original.id) : Promise.resolve(null),
      ]);
      if (activeAnnotationRequestIdRef.current !== requestId) {
        // Zwischenzeitlich wurde ein anderes Foto geöffnet (oder der Dialog geschlossen) — dieses
        // veraltete Ergebnis darf dessen State nicht mehr überschreiben.
        return;
      }
      if (!displayContent) {
        throw new Error('Foto konnte nicht geladen werden');
      }
      setEditorImageDataUrl(`data:${displayContent.mimeType};base64,${displayContent.base64Content}`);
      if (originalContent) {
        setEditorOriginalImageDataUrl(
          `data:${originalContent.mimeType};base64,${originalContent.base64Content}`
        );
      }
    } catch (loadError) {
      if (activeAnnotationRequestIdRef.current === requestId) {
        setEditorError(
          loadError instanceof Error ? loadError.message : 'Foto konnte nicht geladen werden'
        );
      }
    } finally {
      if (activeAnnotationRequestIdRef.current === requestId) {
        setEditorLoading(false);
      }
    }
  };

  const saveAnnotation = async (annotatedDataUrl: string, caption: string) => {
    if (!editingPair) {
      return;
    }
    const pair = editingPair;
    const { original } = pair;
    setSavingAnnotation(true);
    try {
      // Original bleibt IMMER unangetastet — die Markierung wird als eigenständige Datei
      // hochgeladen. Existiert bereits eine markierte Version, ersetzt replaceIfExists sie in
      // place statt eine weitere "-markiert"-Kopie anzulegen (per Nutzer-Rückmeldung).
      const markedFileName = withMarkedFileNameSuffix(original.name);
      const result = await uploadMarkedPhotoAsync(
        projectId,
        getUploadTarget(original),
        annotatedDataUrl,
        markedFileName,
        caption
      );
      onSaved(result, caption, pair, annotatedDataUrl);
      onSuccess?.('Foto gespeichert');
      cancelAnnotation();
    } catch (saveError) {
      onError(saveError instanceof Error ? saveError.message : 'Foto konnte nicht gespeichert werden');
    } finally {
      setSavingAnnotation(false);
    }
  };

  // Per "Löschen" im Editor zurück aufs Original gesprungen und ohne neue Striche auf
  // "Übernehmen" gedrückt — die markierte Version ist dann nur noch ein unverändertes Duplikat
  // des Originals und wird entfernt statt erneut gespeichert (per Nutzer-Rückmeldung).
  const deleteMarkedAnnotation = async () => {
    const pair = editingPair;
    const markedId = pair?.marked?.id;
    if (!pair || !markedId) {
      return;
    }
    setSavingAnnotation(true);
    try {
      await deleteFileAsync(markedId);
      onDeleted(markedId, pair);
      onSuccess?.('Markierung entfernt');
      cancelAnnotation();
    } catch (deleteError) {
      onError(
        deleteError instanceof Error ? deleteError.message : 'Markierung konnte nicht entfernt werden'
      );
    } finally {
      setSavingAnnotation(false);
    }
  };

  return {
    editingPair,
    editorImageDataUrl,
    editorOriginalImageDataUrl,
    editorLoading,
    editorError,
    savingAnnotation,
    openAnnotation,
    cancelAnnotation,
    saveAnnotation,
    deleteMarkedAnnotation,
  };
}
